import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@medikei/ui';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Users,
  Calendar,
  AlertCircle,
  X,
  CreditCard,
} from 'lucide-react';
import { patientsApi } from '@/api';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';

interface Dependent {
  id: string;
  name: string;
  cpf: string;
  birthDate: string;
  relationship: string;
  phone?: string;
  email?: string;
  hasHealthProfile?: boolean;
  createdAt?: string;
}

interface DependentsResponse {
  dependents: Dependent[];
  total: number;
  page: number;
  pageSize: number;
}

const RELATIONSHIP_OPTIONS = [
  'Filho(a)',
  'Cônjuge',
  'Pai/Mãe',
  'Irmão(ã)',
  'Avô(ó)',
  'Neto(a)',
  'Outro',
];

export function DependentsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const limit = 12;

  // Modals state
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingDependent, setEditingDependent] = useState<Dependent | null>(null);
  const [deletingDependent, setDeletingDependent] = useState<Dependent | null>(null);

  // Form field state
  const [formData, setFormData] = useState({
    name: '',
    relationship: 'Filho(a)',
    birthDate: '',
    cpf: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery<DependentsResponse>({
    queryKey: ['patient', 'dependents', page],
    queryFn: async () => {
      const response = await patientsApi.getDependents(page, limit);
      return {
        dependents: response.dependents || [],
        total: response.total || 0,
        page: response.page || 1,
        pageSize: response.pageSize || limit,
      };
    },
  });

  const createMutation = useMutation({
    mutationFn: async (payload: { name: string; relationship: string; birthDate: string; cpf?: string }) => {
      return patientsApi.createDependent(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', 'dependents'] });
      closeFormModal();
    },
    onError: (err: any) => {
      setFormError(err?.response?.data?.message || err?.message || 'Erro ao cadastrar dependente');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: { name: string; relationship: string; birthDate: string; cpf?: string } }) => {
      return patientsApi.updateDependent(id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', 'dependents'] });
      closeFormModal();
    },
    onError: (err: any) => {
      setFormError(err?.response?.data?.message || err?.message || 'Erro ao atualizar dependente');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (dependentId: string) => {
      return patientsApi.deleteDependent(dependentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', 'dependents'] });
      setDeletingDependent(null);
    },
  });

  const openAddModal = () => {
    setEditingDependent(null);
    setFormData({
      name: '',
      relationship: 'Filho(a)',
      birthDate: '',
      cpf: '',
    });
    setFormError(null);
    setIsFormModalOpen(true);
  };

  const openEditModal = (dependent: Dependent) => {
    setEditingDependent(dependent);
    // Extrai apenas a parte da data no formato YYYY-MM-DD
    const isoDate = dependent.birthDate ? dependent.birthDate.split('T')[0] : '';
    setFormData({
      name: dependent.name,
      relationship: dependent.relationship,
      birthDate: isoDate || '',
      cpf: dependent.cpf || '',
    });
    setFormError(null);
    setIsFormModalOpen(true);
  };

  const closeFormModal = () => {
    setIsFormModalOpen(false);
    setEditingDependent(null);
    setFormError(null);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.name.trim()) {
      setFormError('Informe o nome completo do dependente.');
      return;
    }

    if (!formData.birthDate) {
      setFormError('Informe a data de nascimento.');
      return;
    }

    const todayStr = new Date().toISOString().split('T')[0] ?? '';
    if (formData.birthDate > todayStr) {
      setFormError('A data de nascimento não pode estar no futuro.');
      return;
    }

    const formattedBirthDate = formData.birthDate.includes('T')
      ? formData.birthDate
      : `${formData.birthDate}T00:00:00.000Z`;

    const payload = {
      name: formData.name.trim(),
      relationship: formData.relationship,
      birthDate: formattedBirthDate,
      cpf: formData.cpf.trim() || undefined,
    };

    if (editingDependent) {
      updateMutation.mutate({ id: editingDependent.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const filteredDependents = (data?.dependents || []).filter((d) => {
    const term = search.toLowerCase().trim();
    if (!term) return true;
    return (
      d.name?.toLowerCase().includes(term) ||
      d.relationship?.toLowerCase().includes(term) ||
      (d.cpf && d.cpf.includes(term))
    );
  });

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '--';
    const datePart = dateStr.split('T')[0] ?? '';
    const parts = datePart.split('-');
    if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const calculateAge = (birthDate: string) => {
    if (!birthDate) return 0;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return Math.max(0, age);
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 1;
  const todayMaxDate = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6">
      {/* Header com Ação Principal */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
              Dependentes
            </h1>
            {data && data.total > 0 && (
              <Badge variant="outline" className="font-bold text-xs bg-primary/10 text-primary border-primary/20">
                {data.total}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">
            Cadastre dependentes (filhos, cônjuge, pais) para agendar consultas em nome deles
          </p>
        </div>

        <Button
          onClick={openAddModal}
          className="w-full sm:w-auto gap-2 rounded-xl font-bold shadow-md shadow-primary/20 h-11"
        >
          <Plus className="h-4 w-4" />
          Adicionar Dependente
        </Button>
      </div>

      {/* Barra de Busca */}
      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Buscar por nome, parentesco ou CPF..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-input bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all shadow-xs"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground rounded-lg transition-colors"
            aria-label="Limpar busca"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Estados de Carregamento e Erro */}
      {isLoading ? (
        <LoadingOverlay message="Carregando dependentes..." />
      ) : isError ? (
        <ErrorRetry message="Não foi possível carregar seus dependentes." onRetry={() => refetch()} />
      ) : filteredDependents.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-8 sm:p-12 text-center bg-card">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center mx-auto mb-4 shadow-xs">
            <Users className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-1">
            {search ? 'Nenhum dependente encontrado' : 'Nenhum dependente cadastrado'}
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto mb-6">
            {search
              ? `Não encontramos nenhum resultado para "${search}". Tente buscar por outro termo.`
              : 'Cadastre seus dependentes para agendar consultas presenciais ou telemedicina rapidamente com os dados já preenchidos.'}
          </p>
          {search ? (
            <Button variant="outline" onClick={() => setSearch('')} className="rounded-xl font-semibold">
              Limpar busca
            </Button>
          ) : (
            <Button onClick={openAddModal} className="rounded-xl font-bold gap-2 shadow-md shadow-primary/20">
              <Plus className="w-4 h-4" />
              Adicionar Primeiro Dependente
            </Button>
          )}
        </Card>
      ) : (
        <>
          {/* Grade de Cards de Dependentes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDependents.map((dependent) => {
              const age = calculateAge(dependent.birthDate);
              const initials = dependent.name
                ? dependent.name
                    .split(' ')
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                : 'D';

              return (
                <Card
                  key={dependent.id}
                  className="rounded-2xl border border-border bg-card shadow-xs hover:shadow-md hover:border-primary/40 transition-all overflow-hidden flex flex-col justify-between"
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 via-primary/10 to-primary/5 border border-primary/25 flex items-center justify-center text-primary font-extrabold text-base shrink-0 shadow-xs">
                          {initials}
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-base text-foreground truncate" title={dependent.name}>
                            {dependent.name}
                          </h3>
                          <Badge
                            variant="outline"
                            className="mt-1 text-[11px] font-semibold bg-primary/10 text-primary border-primary/20"
                          >
                            {dependent.relationship}
                          </Badge>
                        </div>
                      </div>

                      {/* Botões de Ação */}
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => openEditModal(dependent)}
                          className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/70 rounded-xl transition-colors cursor-pointer"
                          title="Editar dependente"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeletingDependent(dependent)}
                          className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-xl transition-colors cursor-pointer"
                          title="Remover dependente"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2 pt-2 border-t border-border/60 text-xs text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 text-primary shrink-0" />
                        <span className="text-foreground font-medium">
                          {formatDate(dependent.birthDate)} ({age} {age === 1 ? 'ano' : 'anos'})
                        </span>
                      </div>

                      {dependent.cpf && (
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <span>CPF: {dependent.cpf}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-xl font-medium"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Anterior
              </Button>
              <span className="px-3.5 py-1.5 bg-primary/10 text-primary font-bold text-xs rounded-xl border border-primary/20">
                Página {page} de {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded-xl font-medium"
              >
                Próximo
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Modal de Adicionar / Editar Dependente */}
      <Dialog open={isFormModalOpen} onOpenChange={setIsFormModalOpen}>
        <DialogContent className="max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-xl font-bold tracking-tight text-foreground">
              {editingDependent ? 'Editar Dependente' : 'Novo Dependente'}
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              {editingDependent
                ? 'Atualize as informações do dependente cadastrado.'
                : 'Preencha os dados abaixo para cadastrar um novo dependente.'}
            </DialogDescription>
          </DialogHeader>

          {formError && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 text-destructive text-xs rounded-xl flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{formError}</span>
            </div>
          )}

          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div>
              <label htmlFor="dependentName" className="block text-xs font-semibold text-foreground mb-1.5">
                Nome Completo <span className="text-destructive">*</span>
              </label>
              <input
                id="dependentName"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Lucas Martins Silva"
                className="w-full px-3.5 py-2.5 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
                disabled={isSubmitting}
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="dependentRelationship" className="block text-xs font-semibold text-foreground mb-1.5">
                Grau de Parentesco <span className="text-destructive">*</span>
              </label>
              <select
                id="dependentRelationship"
                value={formData.relationship}
                onChange={(e) => setFormData((prev) => ({ ...prev, relationship: e.target.value }))}
                className="w-full px-3.5 py-2.5 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
                disabled={isSubmitting}
              >
                {RELATIONSHIP_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="dependentBirthDate" className="block text-xs font-semibold text-foreground mb-1.5">
                Data de Nascimento <span className="text-destructive">*</span>
              </label>
              <input
                id="dependentBirthDate"
                type="date"
                max={todayMaxDate}
                value={formData.birthDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, birthDate: e.target.value }))}
                className="w-full px-3.5 py-2.5 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="dependentCpf" className="block text-xs font-semibold text-foreground mb-1.5">
                CPF <span className="text-xs text-muted-foreground font-normal">(opcional)</span>
              </label>
              <input
                id="dependentCpf"
                type="text"
                value={formData.cpf}
                onChange={(e) => setFormData((prev) => ({ ...prev, cpf: e.target.value }))}
                placeholder="000.000.000-00"
                maxLength={14}
                className="w-full px-3.5 py-2.5 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
                disabled={isSubmitting}
              />
            </div>

            <DialogFooter className="pt-3 gap-2 flex-col sm:flex-row">
              <Button
                type="button"
                variant="outline"
                onClick={closeFormModal}
                disabled={isSubmitting}
                className="rounded-xl w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                isLoading={isSubmitting}
                className="rounded-xl font-bold shadow-md shadow-primary/20 w-full sm:w-auto"
              >
                {editingDependent ? 'Salvar Alterações' : 'Cadastrar Dependente'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={Boolean(deletingDependent)}
        onOpenChange={(open) => !open && setDeletingDependent(null)}
      >
        <DialogContent className="max-w-sm rounded-2xl border border-border bg-card p-6 shadow-2xl">
          <DialogHeader className="mb-3">
            <div className="w-12 h-12 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center mx-auto mb-3">
              <Trash2 className="w-6 h-6" />
            </div>
            <DialogTitle className="text-lg font-bold text-center text-foreground">
              Remover Dependente
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm text-center text-muted-foreground">
              Tem certeza que deseja remover <strong>{deletingDependent?.name}</strong>? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 flex-col sm:flex-row mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeletingDependent(null)}
              disabled={deleteMutation.isPending}
              className="rounded-xl w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => deletingDependent && deleteMutation.mutate(deletingDependent.id)}
              isLoading={deleteMutation.isPending}
              disabled={deleteMutation.isPending}
              className="rounded-xl font-bold w-full sm:w-auto shadow-md bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sim, Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
